import { MessageService } from 'primeng/api';
import { ProgramacionPendiente } from 'src/app/domain/model/cumplimiento/cumplimiento';
import { environment } from 'src/environments/environment';
import { openExternalWindow } from './window-utils';

const ALERTA_TEMPRANA_WINDOW_FEATURES = 'toolbar=yes,resizable=yes,left=0,top=0,menubar=yes,location=yes,status=yes,scrollbars=yes';

const ALERTA_PARAM_TIPO_DOCUMENTO = 'x_tid';
const ALERTA_PARAM_NUMERO_DOCUMENTO = 'x_nid';
const ALERTA_PARAM_FUENTE = 'x_fte';
const ALERTA_PARAM_FUNCION = 'x_fn';
const ALERTA_PARAM_NMCONSECUTIVO_BENEFICIARIO = 'nmconsecutivoBeneficiario';
const ALERTA_TEMPRANA_FUENTE = 'GIROP';
const ALERTA_TEMPRANA_FUNCION = 'CREAR_AT';

const MSG_AVISO = 'Aviso';
const MSG_DATOS_INCOMPLETOS = 'Datos incompletos';
const MSG_NO_PROGRAMACION_SELECCIONADA = 'No hay una programación seleccionada';
const MSG_NO_DATOS_BENEFICIARIO_ALERTA = 'No se encontraron datos del beneficiario para reportar alerta';
const ALERTA_TEMPRANA_PROXY_URL = `${environment.backend.baseUrl}/servicios/orientacionesProgramadas/alerta-temprana`;

interface ProgramacionAlertaTemprana {
  nmconsecutivoBeneficiario?: number | null;
  dniBeneficiario?: string | null;
  tipoDocumentoBeneficiario?: string | null;
  numeroDocumentoBeneficiario?: string | number | null;
  tipoIdBeneficiario?: string | null;
  nmIdBeneficiario?: string | number | null;
  numeroIdBeneficiario?: string | number | null;
}

function toCleanString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function extraerDesdeDni(dni: string): { tipoDoc: string; numeroDoc: string } {
  const normalizado = toCleanString(dni).toUpperCase();
  const tipoDoc = normalizado.match(/[A-Z]+/)?.[0] ?? '';
  const numeroDoc = normalizado.match(/[0-9]+/)?.[0] ?? '';
  return { tipoDoc, numeroDoc };
}

function resolverDocumentoBeneficiario(programacion: ProgramacionAlertaTemprana): { tipoDoc: string; numeroDoc: string } {
  const tipoDocDirecto = toCleanString(
    programacion.tipoDocumentoBeneficiario ?? programacion.tipoIdBeneficiario
  ).toUpperCase();

  const numeroDocDirecto = toCleanString(
    programacion.numeroDocumentoBeneficiario ?? programacion.nmIdBeneficiario ?? programacion.numeroIdBeneficiario
  );

  const desdeDni = extraerDesdeDni(programacion.dniBeneficiario ?? '');

  return {
    tipoDoc: tipoDocDirecto || desdeDni.tipoDoc,
    numeroDoc: numeroDocDirecto || desdeDni.numeroDoc
  };
}

/** Opciones de apertura de la aplicación externa de alerta temprana. */
export interface AbrirAlertaTempranaOptions {
  /**
   * Permite abrir la aplicación sin datos de beneficiario, para el alta «sin OC».
   *
   * Legacy: RiesgoPotencialViewImpl.onClickNewRp abre URL_ALERTA_TEM sin parámetros
   * desde la botonera de la consulta de alertas tempranas.
   */
  permitirSinProgramacion?: boolean;
}

/**
 * Legacy: CreateOrUpdateCumplimientoViewImpl.onClickNewRp (con beneficiario) y
 * RiesgoPotencialViewImpl.onClickNewRp (sin parámetros, alta «sin OC»).
 *
 * Abre la URL externa de alerta temprana con tipo y número de documento del beneficiario
 * cuando hay una programación; con `permitirSinProgramacion` la abre sin ellos.
 */
export async function abrirAlertaTemprana(
  programacion: ProgramacionPendiente | null | undefined,
  messageService: MessageService,
  options: AbrirAlertaTempranaOptions = {}
): Promise<void> {
  const params = new URLSearchParams({
    [ALERTA_PARAM_FUENTE]: ALERTA_TEMPRANA_FUENTE,
    [ALERTA_PARAM_FUNCION]: ALERTA_TEMPRANA_FUNCION
  });

  if (!programacion) {
    if (!options.permitirSinProgramacion) {
      messageService.add({
        severity: 'warn',
        summary: MSG_AVISO,
        detail: MSG_NO_PROGRAMACION_SELECCIONADA
      });
      return;
    }
    openExternalWindow(`${ALERTA_TEMPRANA_PROXY_URL}?${params.toString()}`, ALERTA_TEMPRANA_WINDOW_FEATURES);
    return;
  }

  const { tipoDoc, numeroDoc } = resolverDocumentoBeneficiario(programacion);
  const nmconsecutivoBeneficiario = programacion.nmconsecutivoBeneficiario;

  if ((!tipoDoc || !numeroDoc) && nmconsecutivoBeneficiario == null) {
    messageService.add({
      severity: 'warn',
      summary: MSG_DATOS_INCOMPLETOS,
      detail: MSG_NO_DATOS_BENEFICIARIO_ALERTA
    });
    return;
  }

  if (tipoDoc) {
    params.set(ALERTA_PARAM_TIPO_DOCUMENTO, tipoDoc);
  }
  if (numeroDoc) {
    params.set(ALERTA_PARAM_NUMERO_DOCUMENTO, numeroDoc);
  }
  if ((!tipoDoc || !numeroDoc) && nmconsecutivoBeneficiario != null) {
    params.set(ALERTA_PARAM_NMCONSECUTIVO_BENEFICIARIO, String(nmconsecutivoBeneficiario));
  }

  openExternalWindow(`${ALERTA_TEMPRANA_PROXY_URL}?${params.toString()}`, ALERTA_TEMPRANA_WINDOW_FEATURES);
}
